import { AUTH_SESSION_KEY, AUTH_USERS_KEY, EXAM_START_DELAY_MS, GRADING, READINESS_DELAY_MS } from '../constants/config'
import { gradeLabel, gradeShortAnswer, scoreObjective, scoreMsqCredit } from '../utils/grading'
import { getQuestionsByType, getRandomQuestions } from './mockQuestions'

const delay = (ms) => new Promise((r) => setTimeout(r, ms))

// ── Syllabus units (replace with API data when backend is ready) ──────────────
export const NISM_UNITS = [
  { unit: 1,  name: 'Concept & Role of a Mutual Fund',                weight: 6  },
  { unit: 2,  name: 'Fund Structure & Constituents',                   weight: 4  },
  { unit: 3,  name: 'Legal & Regulatory Environment',                  weight: 10 },
  { unit: 4,  name: 'Offer Document',                                  weight: 6  },
  { unit: 5,  name: 'Fund Distribution & Channel Management',          weight: 8  },
  { unit: 6,  name: 'Accounting, Valuation & Taxation',                weight: 10 },
  { unit: 7,  name: 'Investor Services',                               weight: 12 },
  { unit: 8,  name: 'Return, Risk & Performance of Funds',             weight: 10 },
  { unit: 9,  name: 'Mutual Fund Scheme Selection',                    weight: 10 },
  { unit: 10, name: 'Selecting the Right Investment Products',         weight: 9  },
  { unit: 11, name: 'Helping Investors with Financial Planning',       weight: 7  },
  { unit: 12, name: 'Recommending Model Portfolios & Financial Plans', weight: 8  },
]

export const MOCK_BANK_STATS = {
  'Concept & Role of a Mutual Fund':                12,
  'Fund Structure & Constituents':                   8,
  'Legal & Regulatory Environment':                 18,
  'Offer Document':                                 10,
  'Fund Distribution & Channel Management':         14,
  'Accounting, Valuation & Taxation':               16,
  'Investor Services':                              20,
  'Return, Risk & Performance of Funds':            15,
  'Mutual Fund Scheme Selection':                   14,
  'Selecting the Right Investment Products':        12,
  'Helping Investors with Financial Planning':      10,
  'Recommending Model Portfolios & Financial Plans':11,
}

// ── Readiness ─────────────────────────────────────────────────────────────────
export async function fetchReadiness() {
  await delay(READINESS_DELAY_MS)
  return { total_attempts: 3, pass_rate: 67, avg_percentage: 58, trend: 'improving' }
}

// ── Exam ──────────────────────────────────────────────────────────────────────
export async function startExam(questionCounts, _difficulty) {
  await delay(EXAM_START_DELAY_MS)
  if (typeof questionCounts === 'object' && questionCounts !== null) {
    return getQuestionsByType(questionCounts)
  }
  return getRandomQuestions(questionCounts)
}

export async function gradeExam(questions, answers) {
  await delay(1200)

  const results = questions.map((q, i) => {
    const userAnswer = answers[i] ?? (q.type === 'msq' ? [] : '')
    if (q.type === 'msq') {
      const creditEarned = scoreMsqCredit(userAnswer, q.correct_answers, Object.keys(q.options ?? {}).length)
      return { question: q, user_answer: userAnswer, is_correct: creditEarned === 1, creditEarned, verdict: null, feedback: null }
    }
    if (q.type === 'mcq' || q.type === 'true_false') {
      return { question: q, user_answer: userAnswer, is_correct: scoreObjective(q, userAnswer), verdict: null, feedback: null }
    }
    const { verdict, feedback } = gradeShortAnswer(userAnswer)
    return { question: q, user_answer: userAnswer, is_correct: null, verdict, feedback }
  })

  // Build topic breakdown
  const breakdown = {}
  for (const r of results) {
    const topic = r.question.topic
    if (!breakdown[topic]) breakdown[topic] = { correct: 0, total: 0 }
    breakdown[topic].total += 1
    if (r.question.type === 'msq')                           breakdown[topic].correct += r.creditEarned ?? 0
    else if (r.is_correct === true || r.verdict === 'Correct')    breakdown[topic].correct += 1
    else if (r.verdict === 'Partially Correct')              breakdown[topic].correct += GRADING.PARTIAL_CREDIT
  }

  // Compute score
  let totalPoints = 0
  for (const r of results) {
    if (r.question.type === 'msq')                              totalPoints += r.creditEarned ?? 0
    else if (r.is_correct === true || r.verdict === 'Correct')    totalPoints += 1
    else if (r.verdict === 'Partially Correct')              totalPoints += GRADING.PARTIAL_CREDIT
  }

  const maxScore = results.length
  const pct      = maxScore > 0 ? (totalPoints / maxScore) * 100 : 0
  const passed   = pct >= GRADING.PASS_CUTOFF_PCT
  const score    = {
    score:              totalPoints,
    max_score:          maxScore,
    percentage:         pct,
    grade:              gradeLabel(pct),
    passed,
    cutoff_percentage:  GRADING.PASS_CUTOFF_PCT,
  }

  const weakTopics = Object.entries(breakdown)
    .filter(([, s]) => s.total > 0 && s.correct / s.total < 0.6)
    .sort(([, a], [, b]) => a.correct / a.total - b.correct / b.total)
    .map(([t]) => t)

  const prepTips = buildPrepTips(weakTopics, pct, maxScore, passed)
  return { results, score, breakdown, weakTopics, prepTips }
}

function buildPrepTips(weakTopics, scorePct, total, passed) {
  const status    = passed ? 'PASS ✅' : 'FAIL ❌'
  const topicsStr = weakTopics.length ? weakTopics.join(', ') : 'none identified'
  return `## Performance Summary
You scored **${scorePct.toFixed(0)}%** on this ${total}-question mock test — a **${status}** relative to the ${GRADING.PASS_CUTOFF_PCT}% cutoff. ${passed ? 'You are on track!' : 'With focused revision you can reach the passing mark.'}

## Focus Areas
${weakTopics.length
    ? weakTopics.map(t => `- **${t}** — revisit core concepts and practise more questions from this area.`).join('\n')
    : '- No major weak areas detected. Keep up the good work!'}

## Study Plan
1. Re-read chapters covering: ${topicsStr}.
2. Attempt 10 targeted questions per weak topic before your next mock.
3. Review all questions you got wrong using the answer review below.
4. Take a full virtual exam to simulate real conditions.`
}

// ── Auth ──────────────────────────────────────────────────────────────────────
const SEED_USERS = [
  {
    id: 0, name: 'Master Admin', email: 'admin@gmail.com', password: 'Admin@123',
    role: 'superadmin',
    createdAt: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 1, name: 'Sandeep', email: 'sandeep@gmail.com', password: 'Sandeep@123',
    role: 'admin', inviteCode: 'SAN1001', status: 'approved', enabled: true,
    createdAt: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 2, name: 'Amal', email: 'amal@gmail.com', password: 'Amal@123',
    role: 'user', enrolledAdminId: 1,
    createdAt: '2026-01-01T00:00:00.000Z',
  },
]

function getUsers() {
  try {
    const stored = JSON.parse(localStorage.getItem(AUTH_USERS_KEY) ?? '[]')
    const merged = SEED_USERS.map(seed => {
      const found = stored.find(u => u.email === seed.email)
      return found ? { ...seed, ...found, role: seed.role, inviteCode: seed.inviteCode } : seed
    })
    for (const u of stored) {
      if (!merged.find(s => s.email === u.email)) merged.push(u)
    }
    return merged
  } catch { return [...SEED_USERS] }
}
function saveUsers(users) { localStorage.setItem(AUTH_USERS_KEY, JSON.stringify(users)) }
function saveSession(s)   { localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(s)) }

function makeSession(user) {
  return {
    id: user.id, name: user.name, email: user.email,
    role: user.role,
    status:          user.status          ?? null,
    inviteCode:      user.inviteCode      ?? null,
    enrolledAdminId: user.enrolledAdminId ?? null,
  }
}

export function getStoredSession() {
  try { return JSON.parse(localStorage.getItem(AUTH_SESSION_KEY)) } catch { return null }
}

export function mockLogout() { localStorage.removeItem(AUTH_SESSION_KEY) }

export function validateInviteCode(code) {
  if (!code?.trim()) return null
  return getUsers().find(u => u.role === 'admin' && u.inviteCode === code.trim().toUpperCase()) ?? null
}

function generateInviteCode(name) {
  const prefix = name.trim().slice(0, 3).toUpperCase().replace(/[^A-Z]/g, 'X').padEnd(3, 'X')
  const digits = String(Math.floor(1000 + Math.random() * 9000))
  return `${prefix}${digits}`
}

export async function mockRegister({ name, email, password, role = 'student', inviteCode }) {
  await delay(500)
  const users = getUsers()
  if (users.find(u => u.email.toLowerCase() === email.toLowerCase()))
    throw new Error('Email already registered')
  if (password.length < 6)
    throw new Error('Password must be at least 6 characters')

  let user
  if (role === 'admin') {
    user = {
      id: Date.now(), name: name.trim(), email: email.trim().toLowerCase(), password,
      role: 'admin', inviteCode: generateInviteCode(name), status: 'pending', enabled: false,
      createdAt: new Date().toISOString(),
    }
  } else {
    const admin = inviteCode?.trim() ? validateInviteCode(inviteCode) : null
    if (inviteCode?.trim() && !admin) throw new Error('Invalid invite code — ask your admin for the correct code')
    user = {
      id: Date.now(), name: name.trim(), email: email.trim().toLowerCase(), password,
      role: 'user', enrolledAdminId: admin?.id ?? null,
      createdAt: new Date().toISOString(),
    }
  }

  saveUsers([...users, user])
  const session = makeSession(user)
  saveSession(session)
  return session
}

export async function mockLogin({ email, password }) {
  await delay(400)
  const users = getUsers()
  const user  = users.find(u => u.email === email.trim().toLowerCase() && u.password === password)
  if (!user) throw new Error('Invalid email or password')
  if (user.role === 'admin' && user.status !== 'approved')
    throw new Error('Your organisation is pending approval. Please wait for the master admin to approve your account.')
  if (user.role === 'admin' && user.enabled === false)
    throw new Error('Your organisation has been disabled. Please contact the master admin.')
  const session = makeSession(user)
  saveSession(session)
  return session
}

// ── Admin (Exams + PDF sources) ───────────────────────────────────────────────
const EXAMS_KEY         = 'skillassess_exams'
const EXAMS_VERSION_KEY = 'skillassess_exams_version'
const EXAMS_SEED_VERSION = 3

const SEED_EXAMS = [
  {
    id: 'exam_1',
    adminId: 1,
    name: 'NISM Series VA – Mutual Fund Distributor',
    description: 'Official NISM certification for mutual fund distributors.',
    createdAt: '2026-01-01T00:00:00.000Z',
    validFrom: '2026-01-01T00:00:00.000Z',
    validTill: '2026-12-31T00:00:00.000Z',
    enabled: true,
    textbookSources: [
      { name: 'NISM_VA_Workbook_Aug2025.pdf', size: 4_200_000, size_display: '4.0 MB' },
    ],
    patternSources: [],
    difficulty: 'Intermediate',
    questionSettings: {
      mcq:          { enabled: true, count: 10, timePerQ: 1 },
      true_false:   { enabled: true, count: 10, timePerQ: 1 },
      short_answer: { enabled: true, count: 10, timePerQ: 1 },
    },
  },
]

function getExams() {
  try {
    const storedVersion = Number(localStorage.getItem(EXAMS_VERSION_KEY) ?? '0')
    if (storedVersion < EXAMS_SEED_VERSION) {
      localStorage.removeItem(EXAMS_KEY)
      localStorage.setItem(EXAMS_VERSION_KEY, String(EXAMS_SEED_VERSION))
    }
    const stored = JSON.parse(localStorage.getItem(EXAMS_KEY) ?? 'null')
    const base   = stored ?? SEED_EXAMS
    const defaultQSettings = {
      mcq:          { enabled: true, count: 10, timePerQ: 1 },
      true_false:   { enabled: true, count: 10, timePerQ: 1 },
      short_answer: { enabled: true, count: 10, timePerQ: 1 },
    }
    return base.map(e => ({
      ...e,
      textbookSources:  e.textbookSources ?? e.sources ?? [],
      patternSources:   e.patternSources  ?? [],
      difficulty:       e.difficulty      ?? 'Intermediate',
      questionSettings: e.questionSettings ?? defaultQSettings,
      enabled:          e.enabled ?? true,
    }))
  } catch { return [...SEED_EXAMS] }
}

function saveExams(exams) {
  localStorage.setItem(EXAMS_KEY, JSON.stringify(exams))
  localStorage.setItem(EXAMS_VERSION_KEY, String(EXAMS_SEED_VERSION))
}

export async function fetchExams(user) {
  await delay(300)
  const exams = getExams()
  if (!user) return []
  if (user.role === 'admin')   return exams.filter(e => e.adminId === user.id || !e.adminId)
  if (user.role === 'user') return exams.filter(e => user.enrolledAdminId ? e.adminId === user.enrolledAdminId : false)
  return []
}

export async function createExam({ name, description, adminId, difficulty, questionSettings, validFrom, validTill }) {
  await delay(300)
  const exams = getExams()
  const exam  = {
    id: `exam_${Date.now()}`, adminId,
    name: name.trim(), description: (description ?? '').trim(),
    createdAt: new Date().toISOString(),
    validFrom: validFrom ?? null,
    validTill: validTill ?? null,
    enabled: true,
    textbookSources: [], patternSources: [],
    difficulty: difficulty ?? 'Intermediate',
    questionSettings: questionSettings ?? {
      mcq:          { enabled: true, count: 10, timePerQ: 1 },
      true_false:   { enabled: true, count: 10, timePerQ: 1 },
      short_answer: { enabled: true, count: 10, timePerQ: 1 },
    },
  }
  exams.push(exam)
  saveExams(exams)
  return exam
}

export async function deleteExam(id) {
  await delay(400)
  saveExams(getExams().filter(e => e.id !== id))
}

export async function updateExam(id, { name, description, validFrom, validTill, enabled }) {
  await delay(300)
  const exams = getExams()
  const exam  = exams.find(e => e.id === id)
  if (!exam) throw new Error('Exam not found')
  exam.name        = name.trim()
  exam.description = (description ?? '').trim()
  if (validFrom !== undefined) exam.validFrom = validFrom
  if (validTill !== undefined) exam.validTill = validTill
  if (enabled  !== undefined) exam.enabled   = enabled
  saveExams(exams)
  return exam
}

export async function uploadToExam(examId, file, sourceKey = 'textbookSources') {
  await delay(800)
  const exams = getExams()
  const exam  = exams.find(e => e.id === examId)
  if (!exam) throw new Error('Exam not found')
  if (!exam[sourceKey]) exam[sourceKey] = []
  const src = {
    name:         file.name,
    size:         file.size,
    size_display: file.size > 1_048_576
      ? `${(file.size / 1_048_576).toFixed(1)} MB`
      : `${Math.round(file.size / 1024)} KB`,
  }
  exam[sourceKey].push(src)
  saveExams(exams)
  return src
}

export async function deleteFromExam(examId, sourceName, sourceKey = 'textbookSources') {
  await delay(400)
  const exams = getExams()
  const exam  = exams.find(e => e.id === examId)
  if (exam) {
    if (!exam[sourceKey]) exam[sourceKey] = []
    exam[sourceKey] = exam[sourceKey].filter(s => s.name !== sourceName)
    saveExams(exams)
  }
}

export async function updateExamSettings(examId, { questionSettings, difficulty }) {
  await delay(300)
  const exams = getExams()
  const exam  = exams.find(e => e.id === examId)
  if (!exam) throw new Error('Exam not found')
  exam.questionSettings = questionSettings
  exam.difficulty       = difficulty
  saveExams(exams)
  return exam
}

export async function reloadEngine() { await delay(1000) }
export async function resetBank()    { await delay(600) }

// ── User management ───────────────────────────────────────────────────────────
const HISTORY_KEY = 'skillassess_history'

const DUMMY_RESULTS = [
  {
    question: { type: 'mcq', question: 'Which regulatory body governs mutual funds in India?', options: { A: 'RBI', B: 'SEBI', C: 'IRDA', D: 'AMFI' }, correct_answer: 'B', topic: 'Legal & Regulatory Environment', explanation: 'SEBI (Securities and Exchange Board of India) is the primary regulator for mutual funds under the SEBI (Mutual Funds) Regulations, 1996.' },
    user_answer: 'B', is_correct: true, verdict: null, feedback: null,
  },
  {
    question: { type: 'mcq', question: 'What is the full form of NAV in mutual funds?', options: { A: 'Net Asset Value', B: 'National Asset Volume', C: 'Nominal Asset Value', D: 'Net Annual Value' }, correct_answer: 'A', topic: 'Concept & Role of a Mutual Fund', explanation: 'NAV stands for Net Asset Value — the per-unit market value of a mutual fund scheme.' },
    user_answer: 'A', is_correct: true, verdict: null, feedback: null,
  },
  {
    question: { type: 'mcq', question: 'An open-ended mutual fund scheme can be redeemed:', options: { A: 'Only on maturity', B: 'Only on stock exchange', C: 'On any business day at NAV', D: 'Only through AMFI' }, correct_answer: 'C', topic: 'Concept & Role of a Mutual Fund', explanation: 'Open-ended schemes allow investors to redeem units at the prevailing NAV on any business day.' },
    user_answer: 'A', is_correct: false, verdict: null, feedback: null,
  },
  {
    question: { type: 'mcq', question: 'Entry load on mutual fund schemes in India was abolished by SEBI in:', options: { A: '2006', B: '2009', C: '2012', D: '2015' }, correct_answer: 'B', topic: 'Legal & Regulatory Environment', explanation: 'SEBI abolished entry loads effective August 1, 2009, to protect investor interests.' },
    user_answer: 'B', is_correct: true, verdict: null, feedback: null,
  },
  {
    question: { type: 'mcq', question: 'Which document contains details of a mutual fund scheme\'s investment objective and risks?', options: { A: 'KYC Form', B: 'Scheme Information Document', C: 'Annual Report', D: 'Portfolio Statement' }, correct_answer: 'B', topic: 'Offer Document', explanation: 'The Scheme Information Document (SID) contains all material information about the scheme including objectives, risks, fees, and investment strategy.' },
    user_answer: 'B', is_correct: true, verdict: null, feedback: null,
  },
  {
    question: { type: 'true_false', question: 'Dividends declared by a mutual fund are guaranteed returns to investors.', correct_answer: 'False', topic: 'Concept & Role of a Mutual Fund' },
    user_answer: 'True', is_correct: false, verdict: null, feedback: null,
  },
  {
    question: { type: 'mcq', question: 'The cut-off time for same-day NAV applicability on liquid fund purchases is:', options: { A: '1:00 PM', B: '2:00 PM', C: '3:00 PM', D: '4:00 PM' }, correct_answer: 'C', topic: 'Investor Services', explanation: 'For liquid and overnight funds, applications received before 3:00 PM with funds credited on the same day get the same-day NAV.' },
    user_answer: 'C', is_correct: true, verdict: null, feedback: null,
  },
  {
    question: { type: 'mcq', question: 'Which type of mutual fund invests primarily in short-term money market instruments?', options: { A: 'Equity Fund', B: 'Liquid Fund', C: 'Balanced Fund', D: 'Index Fund' }, correct_answer: 'B', topic: 'Mutual Fund Scheme Selection', explanation: 'Liquid funds invest in money market instruments with maturity up to 91 days, offering high liquidity and low risk.' },
    user_answer: 'D', is_correct: false, verdict: null, feedback: null,
  },
  {
    question: { type: 'mcq', question: 'AMFI stands for:', options: { A: 'Association of Mutual Fund Investors', B: 'Association of Mutual Funds in India', C: 'Agency for Mutual Fund Investments', D: 'Authority for Mutual Fund India' }, correct_answer: 'B', topic: 'Fund Distribution & Channel Management', explanation: 'AMFI (Association of Mutual Funds in India) is the industry body for mutual funds and is responsible for certifying mutual fund distributors.' },
    user_answer: 'B', is_correct: true, verdict: null, feedback: null,
  },
  {
    question: { type: 'true_false', question: 'SIP (Systematic Investment Plan) allows investors to invest a fixed amount at regular intervals.', correct_answer: 'True', topic: 'Investor Services' },
    user_answer: 'True', is_correct: true, verdict: null, feedback: null,
  },
]

const SEED_HISTORY = [
  { id: 'h1', userId: 2, examId: 'exam_1', examName: 'NISM Series VA – Mutual Fund Distributor', mode: 'practice',
    score: 7, total: 10, passed: true, takenAt: '2026-06-10T09:15:00.000Z', difficulty: 'Intermediate',
    results: DUMMY_RESULTS, grade: 'Good', pct: 70 },
  { id: 'h2', userId: 2, examId: 'exam_1', examName: 'NISM Series VA – Mutual Fund Distributor', mode: 'virtual',
    score: 5, total: 10, passed: false, takenAt: '2026-06-15T14:30:00.000Z', difficulty: 'Intermediate',
    results: DUMMY_RESULTS.slice(0, 10).map((r, i) => i % 3 === 0 ? { ...r, user_answer: 'A', is_correct: false } : r),
    grade: 'Needs Work', pct: 55 },
  { id: 'h3', userId: 2, examId: 'exam_1', examName: 'NISM Series VA – Mutual Fund Distributor', mode: 'practice',
    score: 8, total: 10, passed: true, takenAt: '2026-06-20T11:00:00.000Z', difficulty: 'Advanced',
    results: DUMMY_RESULTS.map((r, i) => i === 2 || i === 7 ? { ...r, user_answer: 'A', is_correct: false } : r),
    grade: 'Excellent', pct: 80 },
]

const HISTORY_SEED_VERSION = 2
const HISTORY_VER_KEY = 'skillassess_history_ver'

function getHistory() {
  try {
    const ver = Number(localStorage.getItem(HISTORY_VER_KEY) ?? 0)
    if (ver < HISTORY_SEED_VERSION) {
      localStorage.removeItem(HISTORY_KEY)
      localStorage.setItem(HISTORY_VER_KEY, String(HISTORY_SEED_VERSION))
    }
    const stored = JSON.parse(localStorage.getItem(HISTORY_KEY) ?? 'null')
    if (stored) return stored
    localStorage.setItem(HISTORY_KEY, JSON.stringify(SEED_HISTORY))
    return [...SEED_HISTORY]
  } catch { return [...SEED_HISTORY] }
}

export function saveResult(userId, { examId, examName, mode, difficulty, score, results, breakdown, prepTips }) {
  const history = getHistory()
  const entry = {
    id:         `h_${Date.now()}`,
    userId,
    examId:     examId ?? null,
    examName:   examName ?? 'Unknown Exam',
    mode,
    difficulty,
    score:      score.correct,
    total:      score.total,
    passed:     score.passed,
    grade:      score.grade,
    pct:        Math.round(score.percentage),
    takenAt:    new Date().toISOString(),
    results,
    breakdown,
    prepTips,
  }
  history.unshift(entry)
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history))
  return entry
}

export function getResultById(id) {
  return getHistory().find(h => h.id === id) ?? null
}

export async function getUsersForAdmin(adminId) {
  await delay(200)
  return getUsers().filter(u => u.role === 'user' && u.enrolledAdminId === adminId)
}

export async function getUserHistory(userId) {
  await delay(200)
  return getHistory().filter(h => h.userId === userId)
}

export async function removeUserFromOrg(userId) {
  await delay(300)
  const users = getUsers()
  const user  = users.find(u => u.id === userId)
  if (!user) throw new Error('User not found')
  user.enrolledAdminId = null
  saveUsers(users)
}

// ── Superadmin ────────────────────────────────────────────────────────────────

export async function getAllOrganisations() {
  await delay(200)
  const users  = getUsers()
  const exams  = getExams()
  const admins = users.filter(u => u.role === 'admin')
  return admins.map(admin => ({
    ...admin,
    status:    admin.status  ?? 'pending',
    enabled:   admin.enabled ?? false,
    examCount: exams.filter(e => e.adminId === admin.id).length,
    userCount: users.filter(u => u.role === 'user' && u.enrolledAdminId === admin.id).length,
  }))
}

export async function getOrgExams(adminId) {
  await delay(200)
  return getExams().filter(e => e.adminId === adminId)
}

export async function getOrgUsers(adminId) {
  await delay(200)
  return getUsers().filter(u => u.role === 'user' && u.enrolledAdminId === adminId)
}

export async function approveOrg(adminId) {
  await delay(200)
  const users = getUsers()
  const admin = users.find(u => u.id === adminId && u.role === 'admin')
  if (!admin) throw new Error('Organisation not found')
  admin.status  = 'approved'
  admin.enabled = true
  saveUsers(users)
}

export async function toggleOrgEnabled(adminId, enabled) {
  await delay(200)
  const users = getUsers()
  const admin = users.find(u => u.id === adminId && u.role === 'admin')
  if (!admin) throw new Error('Organisation not found')
  admin.enabled = enabled
  saveUsers(users)
}

export async function addOrganisation({ name, email, password }) {
  await delay(400)
  const users = getUsers()
  if (users.find(u => u.email.toLowerCase() === email.trim().toLowerCase()))
    throw new Error('Email already registered')
  const user = {
    id: Date.now(), name: name.trim(), email: email.trim().toLowerCase(), password,
    role: 'admin', inviteCode: generateInviteCode(name), status: 'approved', enabled: true,
    createdAt: new Date().toISOString(),
  }
  saveUsers([...users, user])
  return user
}
